import { Component, Input } from '@angular/core';
import { PersonFormComponent } from "../person-form/person-form.component";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { ClrIconModule } from "@clr/angular";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ClarityIcons, twoWayArrowsIcon } from "@cds/core/icon";
import { JsonPipe, NgIf } from "@angular/common";
import { map, switchMap } from "rxjs/operators";
import { AlertService, IAlertType, PersonsStoreService } from "../../../_shared/services";
import { Personen } from "../../../_shared/interfaces";

import { environment as env } from "../../../../environments/environment";

@Component({
  selector: 'app-person-update',
  standalone: true,
  imports: [
    PersonFormComponent,
    RouterLink,
    ClrIconModule,
    NgIf,
    JsonPipe
  ],
  templateUrl: './person-update.component.html',
  styleUrl: './person-update.component.sass'
})

export class PersonUpdateComponent {
  person: Personen[] = [];
  @Input() personData: Personen[] = [];
  personForm: FormGroup;
  devMode: boolean;

  constructor(
    private fb: FormBuilder,
    private pss: PersonsStoreService,
    private router: Router,
    private route: ActivatedRoute,
    private alertDispatcher: AlertService
  ) {
    ClarityIcons.addIcons(twoWayArrowsIcon);
    this.devMode = env.stages.dev;

    this.personForm = this.fb.group({
      pid: 0,
      cid: 0,
      crm_id: [''],
      salutation: [''],
      firstname: ['', Validators.required],
      lastname: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      active: true,
      city: [''],
      country: ['DE'],
      email2: ['', [Validators.email]],
      email3: ['', [Validators.email]],
      mobile: [''],
      phone: [''],
      position: [''],
      postcode: [''],
      region: [''],
      street: [''],
    })
  }

  ngOnInit() {
    this.route.paramMap.pipe(
      map(params => parseInt(params.get('pid') ?? '0', 10)),
      switchMap((pid: number) => this.pss.getSinglePersons(pid))
    )
      .subscribe({
        next: res => {
          this.person = res.object;
          this.personForm.patchValue({
            ...this.person[0],
            country: this.normalizeCountry(this.person[0]?.country)
          });
        },
      error: () => {
        return
      }
    });
  }

  ngOnChanges(): void {
    if (this.personData && Array.isArray(this.personData) && this.personData.length > 0) {
      const person = this.personData[0];
      this.personForm.patchValue({
        ...person,
        country: this.normalizeCountry(person.country)
      });
    }
  }

  onSubmit(): void {
    if (this.personForm.valid) {
      this.updatePerson(this.personForm.value);
    }
  }

  updatePerson(person: Personen) {
    person.country = this.normalizeCountry(person.country);
    person.crm_id = this.normalizeOptionalInteger(person.crm_id);
    this.pss.updatePerson(person)
      .subscribe({
      next: () => {
        this.alertDispatcher.show({
          message: 'Kontakt ' + person.firstname + ' ' + person.lastname + ' (' + person.pid + ') wurde aktualisiert!',
          clrAlertType: IAlertType.INFO
        }, 2000);

      },
      complete: () => {
        this.router.navigate([`person/details/${person.pid}`]);
      },
      error: () => {
        return
      }
    })
  };

  private normalizeCountry(country?: string | null): string {
    if (!country) return '';
    const trimmed = country.trim();
    if (trimmed.length === 2) {
      return trimmed.toUpperCase();
    }
    const map: Record<string, string> = {
      Belgien: 'BE',
      Dänemark: 'DK',
      Deutschland: 'DE',
      Frankreich: 'FR',
      Italien: 'IT',
      Kroatien: 'HR',
      Luxemburg: 'LU',
      Niederlande: 'NL',
      Österreich: 'AT',
      Polen: 'PL',
      Russland: 'RU',
      Schweden: 'SE',
      Schweiz: 'CH',
      Slowakei: 'SK',
      Slowenien: 'SI',
      Tschechien: 'CZ'
    };
    return map[trimmed] || trimmed;
  }

  private normalizeOptionalInteger(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = parseInt(String(value), 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
}
